import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { slidersAPI } from '../services/api';

interface Slide {
  id: number;
  title_fr: string;
  title_en: string;
  subtitle_fr: string;
  subtitle_en: string;
  image_url: string;
  button_text_fr: string;
  button_text_en: string;
  button_url: string;
  active: boolean;
  order_position: number;
}

const HomeSlider = () => {
  const { language = 'en' } = useLanguage();
  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Préchargement des images
  const preloadImages = (imageUrls: string[]) => {
    imageUrls.forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  };

  useEffect(() => {
    fetchSlides();
  }, []);

  useEffect(() => {
    if (slides.length > 0) {
      // Précharger toutes les images
      preloadImages(slides.map((slide) => slide.image_url));

      if (!isPaused) {
        timerRef.current = setInterval(() => {
          setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 5000);
      }
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
  }, [slides, isPaused]);

  const fetchSlides = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await slidersAPI.getAll(true);
      if (response.records && response.records.length > 0) {
        setSlides(response.records);
      } else {
        throw new Error('Aucune diapositive trouvée');
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des diapositives:', error);
      setError('Impossible de charger les diapositives. Affichage des données par défaut.');
      const mockSlides: Slide[] = [
        {
          id: 1,
          title_fr: 'Ride the African Wave',
          title_en: 'Ride the African Wave',
          subtitle_fr: "L'Afrique unie sur la vague. Découvrez la passion du surf africain.",
          subtitle_en: 'Africa united on the wave. Discover the passion of African surfing.',
          image_url:
            'https://images.pexels.com/photos/1212600/pexels-photo-1212600.jpeg?auto=compress&cs=tinysrgb&w=1600',
          button_text_fr: 'Explorer la Tournée',
          button_text_en: 'Explore the Tour',
          button_url: '/africa-surf-tour',
          active: true,
          order_position: 1,
        },
      ];
      setSlides(mockSlides);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    setIsPaused(true);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setIsPaused(true);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setIsPaused(true);
    setCurrentSlide(index);
  };

  // Gestion de la reprise du défilement automatique après interaction
  const handleInteractionEnd = () => {
    setTimeout(() => setIsPaused(false), 3000); // Reprendre après 3 secondes
  };

  if (loading) {
    return (
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-teal-700">
          <div className="absolute inset-0 bg-black/30"></div>
        </div>
        <div className="relative z-10 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">Chargement...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-teal-700">
          <div className="absolute inset-0 bg-black/30"></div>
        </div>
        <div className="relative z-10 text-center">
          <p className="text-white">{error}</p>
        </div>
      </section>
    );
  }

  if (slides.length === 0) {
    return (
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-teal-700">
          <div className="absolute inset-0 bg-black/30"></div>
        </div>
        <div className="relative z-10 text-center">
          <p className="text-white">Aucune diapositive disponible.</p>
        </div>
      </section>
    );
  }

  const currentSlideData = slides[currentSlide];

  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Conteneur d'images avec transition en fondu */}
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <img
            key={slide.id}
            src={slide.image_url}
            alt={language === 'fr' ? slide.title_fr : slide.title_en}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
            loading="lazy"
          />
        ))}
        <div className="absolute inset-0 bg-black/40"></div>
      </div>

      {/* Animated Waves */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden">
        <svg className="relative block w-full h-32" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path
            d="M0,100 C300,120 900,0 1200,20 L1200,120 L0,120 Z"
            className="fill-white opacity-10 animate-pulse"
          />
          <path
            d="M0,80 C300,100 900,-20 1200,0 L1200,120 L0,120 Z"
            className="fill-white opacity-20 animate-pulse animation-delay-2s"
          />
        </svg>
      </div>

      {/* Slide Content */}
      <div className="relative z-10 text-center max-w-4xl mx-auto px-4 transition-all duration-1000">
        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight animate-fade-in">
          {language === 'fr' ? currentSlideData.title_fr : currentSlideData.title_en}
        </h1>
        <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-2xl mx-auto leading-relaxed animate-fade-in-delay">
          {language === 'fr' ? currentSlideData.subtitle_fr : currentSlideData.subtitle_en}
        </p>
        {currentSlideData.button_url && (
          <div className="inline-flex justify-center">
            <a
              href={currentSlideData.button_url}
              className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105"
              aria-label={language === 'fr' ? 'Explorer la diapositive' : 'Explore the slide'}
            >
              <span>
                {language === 'fr' ? currentSlideData.button_text_fr : currentSlideData.button_text_en}
              </span>
            </a>
          </div>
        )}
      </div>

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={() => {
              prevSlide();
              handleInteractionEnd();
            }}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 z-20 w-12 h-12 flex items-center justify-center"
            aria-label="Diapositive précédente"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={() => {
              nextSlide();
              handleInteractionEnd();
            }}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 z-20 w-12 h-12 flex items-center justify-center"
            aria-label="Diapositive suivante"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {/* Slide Indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                goToSlide(index);
                handleInteractionEnd();
              }}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'bg-orange-400 scale-125' : 'bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`Aller à la diapositive ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default HomeSlider;